import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, Input, Button, message } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import TextHighlighter from '$components/TextHighlighter'
import { downloadDwhMaterialSpecFile } from '$api/evoAPIs'

const DwhMaterialsTable = ({
  onSelectionChange,
  selectedDwhKeysFromParent,
  dwhMaterialsData,
  isDwhMaterialNew,
  isLoadingDwhMaterials,
  onRowClick,
}) => {
  const { t } = useTranslation()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    const keysFromParent =
      selectedDwhKeysFromParent instanceof Set
        ? Array.from(selectedDwhKeysFromParent)
        : []
    setSelectedRowKeys(keysFromParent)
  }, [selectedDwhKeysFromParent])

  const handleSelectionChange = (newSelectedKeys) => {
    setSelectedRowKeys(newSelectedKeys)
    if (onSelectionChange) {
      onSelectionChange(new Set(newSelectedKeys))
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectionChange,
    preserveSelectedRowKeys: true,
  }

  const downloadMutation = useMutation({
    mutationFn: downloadDwhMaterialSpecFile,
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      message.success(t('download_successful'))
    },
    onError: () => {
      message.error(t('download_failed'))
    },
  })

  const handleDownload = (materialId) => {
    downloadMutation.mutate(materialId)
  }

  const processedData = useMemo(() => {
    const allItems = dwhMaterialsData?.items || []

    const mappedItems = []
    const unmappedItems = []

    allItems.forEach((item) => {
      if (
        selectedDwhKeysFromParent instanceof Set &&
        selectedDwhKeysFromParent.has(item.id)
      ) {
        mappedItems.push(item)
      } else {
        unmappedItems.push(item)
      }
    })

    const sortedData = [...mappedItems, ...unmappedItems]

    return sortedData.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchText.toLowerCase()),
    )
  }, [dwhMaterialsData?.items, selectedDwhKeysFromParent, searchText])

  const columns = [
    {
      title: t('dwh_material_column_name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const isNew = isDwhMaterialNew(record.id)
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
            }}
          >
            <span
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              <TextHighlighter
                text={text || ''}
                filters={searchText ? [{ value: searchText }] : []}
              />
            </span>
            {selectedDwhKeysFromParent instanceof Set &&
              selectedDwhKeysFromParent.has(record.id) && (
                <span
                  style={{
                    background: '#1890ff',
                    color: 'white',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  SELECTED
                </span>
              )}
            {isNew && (
              <span
                style={{
                  background: '#1890ff',
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontWeight: 'bold',
                }}
              >
                {t('new').toUpperCase()}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: t('description_column'),
      dataIndex: 'description',
      key: 'description',
      responsive: ['lg'],
      ellipsis: true,
      render: (text) => (
        <span style={{ color: '#777', fontStyle: 'italic' }}>{text}</span>
      ),
    },
    {
      title: t('specification_column'),
      key: 'specification',
      render: (record) => {
        if (record.spec_file) {
          return (
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleDownload(record.id)
              }}
              loading={
                downloadMutation.isLoading &&
                downloadMutation.variables === record.id
              }
            >
              {t('download_spec_button')}
            </Button>
          )
        }
        return (
          <Button icon={<DownloadOutlined />} size="small" disabled>
            {t('no_file', 'No File')}
          </Button>
        )
      },
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder={t('search_dwh_material_placeholder')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      {isLoadingDwhMaterials && (
        <div
          style={{
            background: '#f0f9ff',
            border: '1px solid #0ea5e9',
            padding: '12px',
            marginBottom: '12px',
            borderRadius: '4px',
            fontSize: '14px',
            textAlign: 'center',
          }}
        >
          <strong>
            Loading all materials... ({dwhMaterialsData?.items?.length || 0}{' '}
            loaded so far)
          </strong>
        </div>
      )}

      {selectedDwhKeysFromParent instanceof Set &&
        selectedDwhKeysFromParent.size > 0 &&
        !isLoadingDwhMaterials && (
          <div
            style={{
              background: '#e6f4ff',
              border: '1px solid #1890ff',
              padding: '8px 12px',
              marginBottom: '12px',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#1890ff',
            }}
          >
            {t('my_materials.number_of_mapped_materials_are_shown_top', {
              count: selectedDwhKeysFromParent.size,
            })}
          </div>
        )}

      <Table
        tableLayout="fixed"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={processedData}
        loading={isLoadingDwhMaterials}
        rowKey="id"
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) =>
            t('pagination_total_text', {
              start: range[0],
              end: range[1],
              total: total,
            }),
        }}
        onRow={(record) => ({
          onClick: (event) => {
            if (
              event.target.closest('.ant-checkbox-wrapper') ||
              event.target.closest('button')
            ) {
              return
            }
            if (onRowClick) {
              onRowClick(record)
            }
          },
          style: {
            cursor: 'pointer',
            backgroundColor:
              selectedDwhKeysFromParent instanceof Set &&
              selectedDwhKeysFromParent.has(record.id)
                ? '#f0f9ff'
                : undefined,
          },
        })}
        size="small"
      />
    </div>
  )
}

export default DwhMaterialsTable
